import {
  BadgeCheck,
  Brain,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  Heart,
  HeartPulse,
  Pill,
  PillBottle,
  TestTubeDiagonal,
} from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { Popover, YStack } from "tamagui";

import { ACCESS_TRACKS } from "@/access/tracks";
import {
  useGetOrCreateUserProgression,
  useStageAsyncRxClinicRequestFlow,
  useStagePaywallAsyncVisitUserProgressionFlow,
  useStagePaywallVisitUserProgressionFlow,
  useStageTestVisit,
  useTestAccessEnroll,
} from "@/api/generated";
import {
  AccessEnrollmentProgressionRequestFlowName as AccessEnrollmentFlow,
  ProgramID,
} from "@/api/generated/models";
import { Button, ChatIcon, LayoutContext, Separator } from "@/components";
import { Text } from "@/components/Typography";
import { isDev, isLocal } from "@/config/env";
import IDVerificationSidebar from "@/conversation/sidebar/TestGovIdVerification";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import { getShadow } from "@/theme/helpers";
import type { QuestionnaireAnswers } from "./QuestionnaireRenderer";
import QuestionnaireTestPanel from "./QuestionnaireTestPanel";
import PharmacySelect from "./sidebar/PharmacySelect";

// Keeps the menu inside the viewport once a track submenu expands.
const MENU_MAX_HEIGHT = 400;

// One entry per ACCESS track; each track has its own enrollment progression so they can be
// walked independently.
const ACCESS_TRACK_OPTIONS = [
  {
    flowName: AccessEnrollmentFlow.access_eckm_enrollment,
    text: ACCESS_TRACKS[ProgramID.access_eckm].label,
    Icon: HeartPulse,
  },
  {
    flowName: AccessEnrollmentFlow.access_ckm_enrollment,
    text: ACCESS_TRACKS[ProgramID.access_ckm].label,
    Icon: Heart,
  },
  {
    flowName: AccessEnrollmentFlow.access_behavioral_health_enrollment,
    text: ACCESS_TRACKS[ProgramID.access_behavioral_health].label,
    Icon: Brain,
  },
] as const;

const ACCESS_ENROLL_PROGRAMS = [
  {
    programId: ProgramID.access_eckm,
    text: ACCESS_TRACKS[ProgramID.access_eckm].label,
    Icon: HeartPulse,
  },
  {
    programId: ProgramID.access_ckm,
    text: ACCESS_TRACKS[ProgramID.access_ckm].label,
    Icon: Heart,
  },
  {
    programId: ProgramID.access_behavioral_health,
    text: ACCESS_TRACKS[ProgramID.access_behavioral_health].label,
    Icon: Brain,
  },
] as const;

const TestVisitButton = ({
  onPress,
  Icon,
  text,
  ariaLabel,
}: {
  onPress: () => void;
  Icon: React.ElementType;
  text: string;
  ariaLabel: string;
}) => {
  return (
    <Button.Wrapper
      gap="$100"
      padding="$75"
      alignItems="center"
      onPress={() => {
        if (
          typeof window !== "undefined" &&
          window.confirm("Are you sure you want to start a test visit?")
        ) {
          onPress();
        }
      }}
      cursor="pointer"
      borderRadius="$100"
      hoverStyle={{
        backgroundColor: "$navHover",
      }}
      aria-label={ariaLabel}
    >
      <Icon color="$textSecondary" size={16} />
      <Text size="$xs">{text}</Text>
    </Button.Wrapper>
  );
};

const TestButton = ({
  onPress,
  Icon,
  text,
  ariaLabel,
}: {
  onPress: () => void;
  Icon: React.ElementType;
  text: string;
  ariaLabel: string;
}) => {
  return (
    <Button.Wrapper
      gap="$100"
      padding="$75"
      alignItems="center"
      onPress={onPress}
      cursor="pointer"
      borderRadius="$100"
      hoverStyle={{
        backgroundColor: "$navHover",
      }}
      aria-label={ariaLabel}
    >
      <Icon color="$textSecondary" size={16} />
      <Text size="$xs">{text}</Text>
    </Button.Wrapper>
  );
};

export default function TestVisitPopover({
  conversationId,
}: {
  conversationId?: string;
}) {
  const { setModalContent } = useContext(LayoutContext);
  const { mutate: stageTestVisit } = useStageTestVisit();
  const { mutate: stageAsyncRxFlow } = useStageAsyncRxClinicRequestFlow();
  const { mutate: stagePaywallVisitFlow } =
    useStagePaywallVisitUserProgressionFlow();
  const { mutate: stagePaywallAsyncFlow } =
    useStagePaywallAsyncVisitUserProgressionFlow();
  const { mutate: startUserProgression } = useGetOrCreateUserProgression();
  const { mutate: testAccessEnroll } = useTestAccessEnroll();
  const queryClient = useQueryClient();
  const { data: conversationData } = useInfiniteMessages(conversationId);
  const conversation = conversationData?.pages[0]?.conversation;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showAsyncOptions, setShowAsyncOptions] = useState(false);
  const [showAccessEnrollment, setShowAccessEnrollment] = useState(false);
  const [showAccessEnroll, setShowAccessEnroll] = useState(false);
  const [questionnaireAnswers, setQuestionnaireAnswers] =
    useState<QuestionnaireAnswers>({});

  if (!(isDev || isLocal) || !conversationId || conversation?.type !== "nori") {
    return null;
  }

  // Stage an async clinic request whose card opens the paywall_async_visit progression, for the
  // chosen async type. Mirrors the "Payment flow" button but for the async side.
  const stagePaywallAsync = (
    asyncType: "ASYNC_RX" | "ASYNC_DX" | "RX_RENEWAL",
  ) => {
    stagePaywallAsyncFlow(
      {
        noriConversationId: conversation.id,
        params: { async_type: asyncType },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["conversation", "infinite", conversation.id],
            exact: false,
          });
          setIsPopoverOpen(false);
        },
      },
    );
  };

  // Open a track's enrollment progression directly. There's no ACCESS entry point in the product
  // yet, so this stands in for the one that will create the progression and render the flow. The
  // progression isn't scoped to a clinic request, so stage a test visit first if you want the
  // closing clinic redirect to find a visit request to escalate.
  const startAccessEnrollment = (flowName: AccessEnrollmentFlow) => {
    startUserProgression(
      { data: { flow_name: flowName, conversation_id: conversation.id } },
      {
        onSuccess: (progression) => {
          setModalContent(() => (props: any) => (
            <UserProgressionFlow initialProgression={progression} {...props} />
          ));
          setIsPopoverOpen(false);
        },
      },
    );
  };

  return (
    <YStack>
      <Popover
        allowFlip
        stayInFrame
        placement="top-start"
        offset={{ mainAxis: 6, crossAxis: -10 }}
        open={isPopoverOpen}
        onOpenChange={(open) => {
          setIsPopoverOpen(open);
          if (!open) {
            // collapse the submenus when the menu closes
            setShowAsyncOptions(false);
            setShowAccessEnrollment(false);
            setShowAccessEnroll(false);
          }
        }}
      >
        <Popover.Trigger asChild>
          <ChatIcon
            Icon={TestTubeDiagonal}
            mode="tertiary"
            ariaLabel="Start test visit"
            active={isPopoverOpen}
            disableWhenActive={false}
          />
        </Popover.Trigger>
        <Popover.FocusScope>
          <Popover.Content
            borderRadius="$150"
            backgroundColor="$fillCallout"
            {...getShadow()}
            padding="$100"
            maxHeight={MENU_MAX_HEIGHT}
          >
            <Popover.Arrow />
            <Popover.Close />
            <Popover.ScrollView showsVerticalScrollIndicator={false}>
              <YStack gap="$75">
                <TestVisitButton
                  onPress={() => {
                    stageTestVisit(
                      { noriConversationId: conversation.id },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: [
                              "conversation",
                              "infinite",
                              conversation.id,
                            ],
                            exact: false,
                          });
                        },
                      },
                    );
                  }}
                  Icon={ClipboardList}
                  text="Test visit"
                  ariaLabel="test visit"
                />
                <TestButton
                  onPress={() => {
                    stageAsyncRxFlow(
                      { noriConversationId: conversation.id },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: [
                              "conversation",
                              "infinite",
                              conversation.id,
                            ],
                            exact: false,
                          });
                        },
                      },
                    );
                  }}
                  Icon={Pill}
                  text="Async RX"
                  ariaLabel="Async RX"
                />
                <TestButton
                  onPress={() => {
                    stagePaywallVisitFlow(
                      { noriConversationId: conversation.id },
                      {
                        onSuccess: async () => {
                          await queryClient.invalidateQueries({
                            queryKey: [
                              "conversation",
                              "infinite",
                              conversation.id,
                            ],
                            exact: false,
                          });
                          setIsPopoverOpen(false);
                        },
                      },
                    );
                  }}
                  Icon={CreditCard}
                  text="Payment flow"
                  ariaLabel="Payment flow"
                />
                <TestButton
                  onPress={() => setShowAsyncOptions((open) => !open)}
                  Icon={showAsyncOptions ? ChevronDown : ChevronRight}
                  text="Async payment flow"
                  ariaLabel="Async payment flow"
                />
                {showAsyncOptions && (
                  <YStack paddingLeft="$150" gap="$75">
                    <TestButton
                      onPress={() => stagePaywallAsync("ASYNC_RX")}
                      Icon={Pill}
                      text="RX"
                      ariaLabel="Async paywall RX"
                    />
                    <TestButton
                      onPress={() => stagePaywallAsync("ASYNC_DX")}
                      Icon={ClipboardList}
                      text="DX"
                      ariaLabel="Async paywall DX"
                    />
                    <TestButton
                      onPress={() => stagePaywallAsync("RX_RENEWAL")}
                      Icon={PillBottle}
                      text="Renewal"
                      ariaLabel="Async paywall Renewal"
                    />
                  </YStack>
                )}
                <Separator borderColor="$backgroundGround" />
                {/* TEMP: Fixture-only launcher for reviewing the questionnaire UI. */}
                <TestButton
                  onPress={() => {
                    setModalContent(() => (props: any) => (
                      <QuestionnaireTestPanel
                        {...props}
                        initialAnswers={questionnaireAnswers}
                        onAnswersChange={setQuestionnaireAnswers}
                      />
                    ));
                    setIsPopoverOpen(false);
                  }}
                  Icon={ClipboardList}
                  text="Questionnaire UI"
                  ariaLabel="Open questionnaire UI"
                />
                <Separator borderColor="$backgroundGround" />
                <TestButton
                  onPress={() => {
                    setModalContent(() => (props: any) => (
                      <IDVerificationSidebar {...props} />
                    ));
                    setIsPopoverOpen(false);
                  }}
                  Icon={BadgeCheck}
                  text="Test ID Verification"
                  ariaLabel="test id verification"
                />
                <Separator borderColor="$backgroundGround" />
                <TestButton
                  onPress={() => {
                    setModalContent(() => (props: any) => (
                      <PharmacySelect {...props} />
                    ));
                    setIsPopoverOpen(false);
                  }}
                  Icon={PillBottle}
                  text="Open pharmacy search"
                  ariaLabel="Open pharmacy search"
                />
                <Separator borderColor="$backgroundGround" />
                <TestButton
                  onPress={() => setShowAccessEnrollment((open) => !open)}
                  Icon={showAccessEnrollment ? ChevronDown : ChevronRight}
                  text="ACCESS enrollment"
                  ariaLabel="ACCESS enrollment"
                />
                {showAccessEnrollment && (
                  <YStack paddingLeft="$150" gap="$75">
                    {ACCESS_TRACK_OPTIONS.map(({ flowName, text, Icon }) => (
                      <TestButton
                        key={flowName}
                        onPress={() => startAccessEnrollment(flowName)}
                        Icon={Icon}
                        text={text}
                        ariaLabel={`ACCESS ${text} enrollment`}
                      />
                    ))}
                  </YStack>
                )}
                <Separator borderColor="$backgroundGround" />
                <TestButton
                  onPress={() => setShowAccessEnroll((open) => !open)}
                  Icon={showAccessEnroll ? ChevronDown : ChevronRight}
                  text="ACCESS alignment"
                  ariaLabel="ACCESS alignment"
                />
                {showAccessEnroll && (
                  <YStack paddingLeft="$150" gap="$75">
                    {ACCESS_ENROLL_PROGRAMS.map(({ programId, text, Icon }) => (
                      <TestButton
                        key={programId}
                        onPress={() =>
                          testAccessEnroll(
                            { program: programId },
                            { onSuccess: () => setIsPopoverOpen(false) },
                          )
                        }
                        Icon={Icon}
                        text={text}
                        ariaLabel={`ACCESS enroll ${text}`}
                      />
                    ))}
                  </YStack>
                )}
              </YStack>
            </Popover.ScrollView>
          </Popover.Content>
        </Popover.FocusScope>
      </Popover>
    </YStack>
  );
}
